import {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {ModelingMethodName} from "../../logic/utils";

import {deleteNodeKeys, TFormulaNodeProps} from "./logic/formulaNode";
import * as css from "./styles/css.module.scss";

import {
    TDriverOperationalMonthlyAverageMetric
} from "../../../../../../contexts/account/data/logic/driversAndWorksheetData";
import {titleCase} from "../../../../../../global/string_functions";
import MonthlyRangeMenu from "../formula-menu/operational-monthly-average/MonthlyRangeMenu";
import {Property} from "../../../../../../contexts/properties/logic";
import {getMonths} from "../../../../../../utils/months-helper";
import {splitDate} from "../../../../../../global/date_functions";
import MonthlyOrYearlyGrowthMenu from "../formula-menu/operational-monthly-average/MonthlyOrYearlyGrowthMenu";
import {isEmpty} from "../../../../../../utils/exports";

function renderMonths(driver: TDriverOperationalMonthlyAverageMetric, property?: Property): string {
    const [startYear, startMonth] = splitDate(driver.startMonth);
    const [endYear, endMonth] = splitDate(driver.endMonth);
    const months = getMonths(property);

    let sYear: number | string = startYear;
    let eYear: number | string = endYear;
    if(property && property.isAcquisitionProperty) {
        sYear = "T12";
        eYear = "T12";
    }

    return `${months[startMonth - 1]} ${sYear} to ${months[endMonth - 1]} ${eYear}`;
}

function renderGrowth(driver: TDriverOperationalMonthlyAverageMetric): string {
    if(!isEmpty(driver.monthlyGrowth)) {
        return `${driver.monthlyGrowth}% monthly growth`;
    } else if(!isEmpty(driver.yearlyGrowth)) {
        return `${driver.yearlyGrowth}% yearly growth`;
    } else if(driver.customIncrease === true) {
        return "custom monthly increase";
    }

    return "no growth";
}

export function renderAsString(driver: TDriverOperationalMonthlyAverageMetric): string {
    const driverName = titleCase(driver.sourceMetric.split("_").join(" "));
    return `${driverName} ${renderMonths(driver)} with ${renderGrowth(driver)}`;
}

export type TOperationalFormulaNodeProps = TFormulaNodeProps&{
    isBulkUpdateBar?: boolean;
    isPropertyDriversUI?: boolean;
    editableFxBarChecker: ((modelingMethod: ModelingMethodName, modelingMethodLocked: boolean) => boolean) | undefined;
    selectedAccountIds: string[];
    driver: TDriverOperationalMonthlyAverageMetric;
    property: Property | undefined;
}

export function OperationalMonthlyAverageDriverFxNode(props: TOperationalFormulaNodeProps): ReactElement {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [innerElementHovered, setInnerElementHovered] = useState<boolean>(false);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isFocused && wrapperRef.current) {
            wrapperRef.current.focus();
        }
    }, [isFocused]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                    wrapperRef.current &&
                    (wrapperRef.current === event.target ||
                            wrapperRef.current.contains(event.target as Node))
            ) {
                return;
            }
            setIsFocused(false);
        };

        // Clicking outside the current node should remove focus
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClick = () => {
        setIsFocused(!isFocused);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (
                isFocused && deleteNodeKeys.includes(e.key) && !innerElementHovered
        ) {
            props.fbUpdates.removeOperationalMonthlyAverageDriver();
        }
    };

    const driverNameRender: string|ReactElement = titleCase(props.driver.sourceMetric.split("_").join(" "));

    const nodeClassName = props.locked ? css.fxNodeLocked : css.fxNode;

    const className = useMemo(() => {
        const allClassNames = [nodeClassName, css.deletableNode];

        if (props.className) {
            allClassNames.push(props.className);
        }

        if (isFocused) {
            allClassNames.push(css.focusedNode);
        }

        if (innerElementHovered) {
            allClassNames.push(css.disableHover);
        } else {
            allClassNames.filter((each: string) => each !== css.disableHover);
        }

        return allClassNames.join(' ');
    }, [isFocused, innerElementHovered, nodeClassName]);

    function updateDriver(partial: Partial<TDriverOperationalMonthlyAverageMetric>): void {
        props.fbUpdates.updateOperationalMonthlyAverageDriver({
            ...props.driver,
            ...partial
        });
    }

    return (
            <div
                    className={className}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    /**
                     * onMouseLeave() isn't always triggered when the user selects a lookback period
                     * and the lookback menu closes, so we make sure by doing this:
                    */
                    onMouseEnter={() => setInnerElementHovered(false)}
                    ref={wrapperRef}
                    tabIndex={0}
            >
                (Op Monthly Avg of {driverNameRender}:&nbsp;
                <div
                        className={css.fxNode}
                        onMouseEnter={() => setInnerElementHovered(true)}
                        onMouseLeave={() => setInnerElementHovered(false)}
                >
                    {props.property ?
                        <MonthlyRangeMenu
                            property={props.property}
                            startMonthDate={props.driver.startMonth}
                            endMonthDate={props.driver.endMonth}
                            startMonthDateSet={val => {updateDriver({startMonth: val});}}
                            endMonthDateSet={val => {updateDriver({endMonth: val});}}
                            triggerNode={<a aria-role="button" aria-description="select month range for driver">{renderMonths(props.driver, props.property)}</a>}
                        /> :
                        renderMonths(props.driver, props.property)
                    }
                </div>
                &nbsp;with&nbsp;
                <div
                        className={css.fxNode}
                        onMouseEnter={() => setInnerElementHovered(true)}
                        onMouseLeave={() => setInnerElementHovered(false)}
                >
                    <MonthlyOrYearlyGrowthMenu
                            driver={props.driver}
                            monthlyGrowthSet={val => updateDriver({monthlyGrowth: val?.toString(), yearlyGrowth: undefined, customIncrease: undefined})}
                            yearlyGrowthSet={val => updateDriver({yearlyGrowth: val?.toString(), monthlyGrowth: undefined, customIncrease: undefined})}
                            customIncreaseSet={val => updateDriver({customIncrease: val, yearlyGrowth: undefined, monthlyGrowth: undefined})}
                            clearGrowth={() => updateDriver({monthlyGrowth: undefined, yearlyGrowth: undefined, customIncrease: undefined})}
                            triggerNode={<a aria-role="button" aria-description="select monthly or yearly growth for driver">{renderGrowth(props.driver)}</a>}
                    />
                </div>)
            </div>
    );
}
